import React, { useEffect, useState } from "react";
import { FaSpinner } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Batch = {
  id: number;
  name: string;
  topic: string;
};

type HomeworkSummaryInfo = {
  homeworkId: number;
  chapterNumber: number;
  level: string;
  title: string;
};

type HomeworkScore = {
  homeworkId: number;
  score: number | null;
  completed: boolean;
};

type StudentHomeworkSummary = {
  studentId: number;
  firstName: string;
  lastName: string;
  email: string;
  scores: HomeworkScore[];
};

type BatchHomeworkSummary = {
  homeworks: HomeworkSummaryInfo[];
  students: StudentHomeworkSummary[];
};

const MentorBatchHomeworkSummary: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [summary, setSummary] = useState<BatchHomeworkSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    setLoadingBatches(true);
    setError("");
    try {
      // Fetch mentor's batches
      const response = await fetch(`${API_BASE_URL}/api/mentor/my-batches`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        // Map to extract id, name, topic
        const batchList = data.map((b: any) => ({
          id: b.id,
          name: b.name,
          topic: b.topic,
        }));
        setBatches(batchList);
        // Auto-select first batch
        if (batchList.length > 0) {
          setSelectedBatchId(batchList[0].id);
        }
      } else {
        setError("Failed to load batches");
      }
    } catch (err) {
      console.error("Error fetching batches:", err);
      setError("Failed to load batches");
    } finally {
      setLoadingBatches(false);
    }
  };

  useEffect(() => {
    if (!selectedBatchId) {
      setSummary(null);
      return;
    }
    fetchSummary(selectedBatchId);
  }, [selectedBatchId]);

  const fetchSummary = async (batchId: number) => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/homework/batches/${batchId}/summary`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      } else {
        setSummary(null);
        setError("Failed to load homework summary");
      }
    } catch (err) {
      console.error("Error fetching summary:", err);
      setSummary(null);
      setError("Failed to load homework summary");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Batch Selector */}
      <div className="mb-6">
        <label className="font-bold text-gray-700 mr-3">Select Batch:</label>
        <select
          className="border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
          style={{ fontSize: "16px" }}
          onChange={(e) => setSelectedBatchId(Number(e.target.value))}
          value={selectedBatchId ?? ""}
          disabled={loadingBatches}
        >
          {loadingBatches ? (
            <option>Loading...</option>
          ) : (
            <>
              {batches.length === 0 && (
                <option value="">No batches available</option>
              )}
              {batches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.topic})
                </option>
              ))}
            </>
          )}
        </select>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
          <span className="ml-3 text-gray-600">
            Loading homework summary...
          </span>
        </div>
      ) : selectedBatchId && summary ? (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border font-sans text-sm">
            <thead>
              <tr className="bg-[#009fe3] text-white">
                <th className="border px-4 py-2 text-left font-semibold sticky left-0 bg-[#009fe3] z-10">
                  Student
                </th>
                {summary.homeworks.map((hw) => (
                  <th
                    key={hw.homeworkId}
                    className="border px-4 py-2 text-center font-semibold"
                  >
                    Kapitel {hw.chapterNumber}
                    <div className="text-xs font-normal opacity-90">
                      ({hw.level})
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary.students.length === 0 ? (
                <tr>
                  <td
                    colSpan={summary.homeworks.length + 1}
                    className="border px-4 py-8 text-center text-gray-400"
                  >
                    No students enrolled in this batch
                  </td>
                </tr>
              ) : (
                summary.students.map((student) => (
                  <tr key={student.studentId} className="hover:bg-blue-50">
                    <td className="border px-4 py-2 font-semibold text-gray-900 sticky left-0 bg-white">
                      {student.firstName} {student.lastName}
                    </td>
                    {summary.homeworks.map((hw) => {
                      const score = student.scores.find(
                        (s) => s.homeworkId === hw.homeworkId
                      );

                      if (!score) {
                        return (
                          <td
                            key={hw.homeworkId}
                            className="border px-4 py-2 text-center"
                          >
                            <span className="text-gray-400">—</span>
                          </td>
                        );
                      }

                      return (
                        <td
                          key={hw.homeworkId}
                          className="border px-4 py-2 text-center"
                        >
                          {score.completed && score.score !== null ? (
                            <span className="font-semibold text-green-600">
                              {score.score.toFixed(1)}/20
                            </span>
                          ) : (
                            <span className="text-red-500 font-medium">x</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && (
          <div className="text-center py-12 text-gray-500">
            {selectedBatchId
              ? "No homework summary available for this batch."
              : "Please select a batch to view homework summary."}
          </div>
        )
      )}
    </div>
  );
};

export default MentorBatchHomeworkSummary;
